import { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { usePortSearch, type Port } from '@/hooks/usePorts';
import { Anchor, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface PortAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelectPort?: (port: Port) => void;
  placeholder?: string;
  id?: string;
  name?: string;
}

const PortAutocomplete = ({
  value,
  onChange,
  onSelectPort,
  placeholder,
  id,
  name,
}: PortAutocompleteProps) => {
  const { t } = useTranslation('ports');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || '');
  const containerRef = useRef<HTMLDivElement>(null);
  const { results, loading } = usePortSearch(query);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const handleSelect = (port: Port) => {
    onChange(port.nom);
    onSelectPort?.(port);
    setQuery(port.nom);
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <Input
        id={id}
        name={name}
        placeholder={placeholder ?? t('autocomplete.placeholder')}
        value={query}
        autoComplete="off"
        onChange={(e) => {
          setQuery(e.target.value);
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => query.length >= 2 && setOpen(true)}
      />
      {open && query.trim().length >= 2 && (
        <div className="absolute z-50 mt-1 w-full max-h-64 overflow-auto rounded-md border bg-popover shadow-lg">
          {loading && (
            <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> {t('autocomplete.searching')}
            </div>
          )}
          {!loading && results.length === 0 && (
            <div className="px-3 py-2 text-sm text-muted-foreground">{t('autocomplete.noResults')}</div>
          )}
          {!loading && results.map((p) => (
            <button
              type="button"
              key={p.id}
              onClick={() => handleSelect(p)}
              className="flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
            >
              <Anchor className="h-4 w-4 mt-0.5 text-primary shrink-0" />
              <div className="min-w-0">
                <div className="font-medium truncate">{p.nom}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {[p.ville, p.code_postal, p.departement].filter(Boolean).join(' · ')}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PortAutocomplete;
